import React from 'react';
import { useSelector } from 'react-redux';
import PropTypes from 'prop-types';
import { Helmet } from 'react-helmet';
import { getLocaleData } from '@/selectors/localeSelectors';
import { useRouteParams } from '@/context/routeParamsContext';
import { useOneCmsLocaleOverviewHeader } from '@/hooks/oneCmsIntlHooks/oneCmsIntlHelperHooks';
import { getEnvLevel } from '@/selectors/originSelectors';
import { getMarketName } from '@/helpers/utils';
import { supportedCountries } from '@/constants/supportedLocales';

const getPageTitlePrefixByEnvLevel = (envLevel) => {
  switch (envLevel) {
    case 'E0':
      return 'E0 ';
    case 'E1':
      return 'E1 ';
    case 'E2':
      return 'E2 ';
    default:
      return '';
  }
};

const productsHiddenWithRobots = [
  'the-platinum-card-aeme',
  'the-centurion-card-aeme',
  'the-platinum-card-asal',
  'the-centurion-card-asal',
  'the-platinum-card-cmbc',
  'the-centurion-card-cmbc',
  'the-platinum-card-argentina',
  'the-centurion-card-argentina',
  'the-platinum-card-idc-en',
  'the-platinum-card-idc-es',
  'the-centurion-card-idc-en',
  'the-centurion-card-idc-es',
  'the-centurion-card-israel',
  'the-platinum-card-israel',
  'the-centurion-card-sa',
  'the-platinum-card-sa',
  'the-platinum-card-rsb',
  'the-centurion-card-rsb',
  'the-centurion-card-swisscard',
  'the-platinum-card-swisscard',
  'the-centurion-card',
];

const defaultNoLocalePageTitle = 'Global Lounge Collection';
const defaultNoLocalePageTitleSuffix = 'American Express';

export const LoungeFinderHelmets = ({ location }) => {
  const { product } = useRouteParams();
  const localeHeader = useOneCmsLocaleOverviewHeader();
  const { locale } = useSelector(getLocaleData);
  const envLevel = useSelector(getEnvLevel);
  const marketName = getMarketName(locale);

  const { pathname } = location;
  const localeAllLower = locale.toLowerCase();
  // todo: logic for old urls, which can be removed after the SEO migration
  const canonicalPath = pathname.startsWith('/lounge-access')
    ? `/${localeAllLower}/travel/lounges${pathname.replace('/lounge-access', '')}`
    : pathname;

  const supportedLocaleList = supportedCountries.filter(
    (market) =>
      market.products.includes(product) && market.locale !== localeAllLower
  );

  return (
    <>
      <Helmet
        defaultTitle={
          localeHeader ||
          `${defaultNoLocalePageTitle} - ${defaultNoLocalePageTitleSuffix}`
        }
        titleTemplate={`${getPageTitlePrefixByEnvLevel(envLevel)}%s | Amex ${marketName}`}
        {...{
          htmlAttributes: {
            lang: localeAllLower,
          },
          link: [
            {
              rel: 'stylesheet',
              href: 'https://www.aexp-static.com/cdaas/dls/packages/@americanexpress/dls/7.4.0/stylesheets/dls-core.min.css',
            },
            {
              rel: 'icon',
              href: 'https://www.americanexpress.com/favicon.ico',
            },
            {
              rel: 'apple-touch-icon',
              sizes: '152x152',
              href: 'https://www.americanexpress.com/apple-touch-icon-152x152.png',
            },
            {
              rel: 'canonical',
              href: `https://www.americanexpress.com${canonicalPath}`,
            },
            ...supportedLocaleList.map((market) => ({
              rel: 'alternate',
              href: `https://www.americanexpress.com${canonicalPath.replace(`/${localeAllLower}/`, `/${market.locale}/`)}`,
              hreflang: market.locale,
            })),
          ],
          bodyAttributes: {},
          meta: [
            { 'http-equiv': 'X-UA-Compatible', content: 'IE=edge' },
            { charset: 'utf-8' },
            {
              name: 'viewport',
              content: 'width=device-width, initial-scale=1',
            },
            { name: 'application-name', content: 'American Express' },
            { name: 'keywords', content: 'one-amex' },
          ],
        }}
      >
        <title>{localeHeader || defaultNoLocalePageTitle}</title>
      </Helmet>
      {productsHiddenWithRobots.includes(product) && (
        <Helmet>
          <meta name="robots" content="noindex" />
        </Helmet>
      )}
    </>
  );
};

LoungeFinderHelmets.propTypes = {
  location: PropTypes.shape({
    pathname: PropTypes.string,
  }).isRequired,
};
